"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { Briefcase, Link } from "lucide-react"
import { useGetJobs } from "openapi/api/endpoints"

interface JobLinkDialogProps {
  isOpen: boolean
  onClose: () => void
  /**
   * The candidate whose Yes is waiting on this, when there is one. Absent when
   * the dialog was opened to change the job rather than to land an add — the
   * copy and the second button both follow from it.
   */
  candidateName?: string
  linkJobAndSearch: (selectedJobId: string) => void
}

export function JobLinkDialog({
  isOpen,
  onClose,
  candidateName,
  linkJobAndSearch,
}: JobLinkDialogProps) {
  const [selectedJobId, setSelectedJobId] = useState<string>("")
  const {data:jobs}=useGetJobs()
  const availableJobs=jobs?.filter(job=>!job.associated_search_id)

  const handleLink = () => {
    if (selectedJobId) linkJobAndSearch(selectedJobId)
  }

  return (
    <DialogShell
      open={isOpen}
      onOpenChange={onClose}
      icon={<Link className="size-4" />}
      title="Link Search to Job"
      description={
        candidateName
          ? `You are about to add ${candidateName} to a job. Pick the job this search feeds — every later Yes lands there too.`
          : "Pick the job this search feeds. Every Yes lands on it, at longlist."
      }
      footer={
        /*
          One button. "Just Add This Candidate (Don't Link)" was the other one,
          and there is nowhere for an unlinked add to go — the pipeline write
          needs a job id, which is precisely what this dialog is for.
        */
        <PageAction tone="primary" onClick={handleLink} disabled={!selectedJobId}>
          <Link className="size-[15px]" />
          {candidateName ? "Link to job and add" : "Link this search to the job"}
        </PageAction>
      }
    >
      <FieldShell label="Job">
        <Select value={selectedJobId} onValueChange={setSelectedJobId}>
          <SelectTrigger
            aria-label="Job"
            className={cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")}
          >
            <SelectValue placeholder="Choose a job to link this search to..." />
          </SelectTrigger>
          <SelectContent>
            {availableJobs?.map((job) => (
              <SelectItem key={job.id} value={job.id}>
                <span className="flex items-center gap-2">
                  <Briefcase className="size-3.5 flex-none" />
                  <span className="truncate">{job.title}</span>
                  {job.department && (
                    <span className="text-[11px] text-muted-foreground">{job.department}</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldShell>
    </DialogShell>
  )
}
